import {computed, makeObservable} from "mobx";

import type {ItemModelType, WorkspaceModelType} from "@stores/WorkspaceStore";
import {TreeItemMove, TreeSource} from "../../components/Tree/TreeSource";


import type {AppState} from "@stores/AppState";
import type {ITreeItem} from "../../components/Tree/types";
import {DocumentTreeItem} from "../../components/Tree/DocumentTreeItem";

import {isPinned} from '../../../utils/isPinned';
import {captureMessageWrapper} from "../../../utils/errors/captureMessage";
import {moveItemUndoable} from "../../../actions/item/moveItem";
import {CrudServices} from "../../../platform/services/types/ServiceDependencies";


export interface DocumentTreeSourceDeps {
    crudServices: CrudServices;
    appState: AppState;
}

export interface DocumentItem extends ITreeItem<DocumentItem> {
    childrenIds: string[];
    isCluster: boolean,
    data: {
        item: ItemModelType,
        hadRecursion: boolean,
        parentItemId: string | null,
        parentIdPath: string | null,
    }
}

export class DocumentTreeSource extends TreeSource<DocumentItem> {
    wsMST: WorkspaceModelType;
    _reachableItemId: Set<string> = new Set();
    deps: DocumentTreeSourceDeps;

    getTopLevelItems(renderingRootId: string = this.wsMST.view): DocumentItem[] {
        const rootItem = this.data.items[renderingRootId] ?? this.data.items[this.wsMST.view];
        if (!rootItem) {
            return [];
        }

        return rootItem.childrenIds.map(itemId => {
            return this.data.items[itemId];
        }).filter(Boolean);
    }

    handleMove(moveParams: TreeItemMove): void {
        const removeSourceTreeItem = this.data.items[moveParams.movedItemId];
        const sourceParent = this.data.items[removeSourceTreeItem.parentId];
        const sourceItemIndex = sourceParent.childrenIds.findIndex((treeItemId) => treeItemId === removeSourceTreeItem.id);

        const destinationParent = (
            moveParams.dropType === 'insertChild'
                ? this.data.items[moveParams.dropTargetId]
                : this.data.items[this.data.items[moveParams.dropTargetId].parentId]
        );

        const isDestinationTheSame = sourceParent.id === destinationParent.id;

        // Calculate the index after considering if we need to remove the source item
        const localDestinationIndex = (() => {
            if (moveParams.dropType === 'insertChild') {
                return 0;
            } else {
                // Create a temporary array that reflects the state after source removal if needed
                let childrenIds = [...destinationParent.childrenIds];
                if (isDestinationTheSame) {
                    childrenIds = childrenIds.filter(id => id !== removeSourceTreeItem.id);
                }

                const actionTargetIndex = childrenIds.findIndex((treeItemId) => treeItemId === moveParams.dropTargetId);
                if (moveParams.dropType === 'insertBefore') {
                    return actionTargetIndex;
                } else {
                    return actionTargetIndex + 1;
                }
            }
        })();

        const activeWorkspace = this.deps.appState.rootStore.view.workspace;
        if (!activeWorkspace) {
            return;
        }

        const sourceItem = removeSourceTreeItem.data.item;
        const sourceParentChilds = sourceParent.childrenIds.map(treeId => this.data.items[treeId].data.item.id);

        let updatedDestinationChildIds = destinationParent.childrenIds.map(treeId => this.data.items[treeId].data.item.id);
        if (isDestinationTheSame) {
            updatedDestinationChildIds = updatedDestinationChildIds.filter(itemId => itemId !== sourceItem.id);
        }
        updatedDestinationChildIds.splice(localDestinationIndex, 0, removeSourceTreeItem.data.item.id);

        moveItemUndoable(
            this.deps.crudServices,
            this.deps.appState,
            {
                localDestinationIndex: localDestinationIndex,
                sourceItem: sourceItem,
                isDestinationTheSame: isDestinationTheSame,
                isSourceWorkspaceRoot: isWorkspaceRootId(sourceParent.id),
                isDestinationWorkspaceRoot: isWorkspaceRootId(destinationParent.id),
                sourceChilds: (isDestinationTheSame ? updatedDestinationChildIds : sourceParentChilds.filter(itemId => itemId !== sourceItem.id)),
                sourceParentItemId: sourceParent.data.item?.id,
                destinationChilds: updatedDestinationChildIds,
                destinationParentItemId: destinationParent.data.item?.id,
            },
            activeWorkspace,
            this.data.pinnedLength,
            sourceItemIndex
        )
    }

    toggle(treeItemId: string, newState?: boolean): void {
        const treeItem = this.data.items[treeItemId];
        const itemMST = treeItem.data.item;
        if (newState === undefined) {
            itemMST.setExpanded(!itemMST.isExpanded);
        } else {
            itemMST.setExpanded(newState)
        }
    }

    constructor(deps: DocumentTreeSourceDeps, workspace: WorkspaceModelType) {
        super();
        this.wsMST = workspace;
        this.deps = deps;

        makeObservable(this, {
            data: computed,
        })
    }

    get data() {
        const items: Record<string, DocumentItem> = {};
        let pinnedLength = 0;
        let itemIds: string[];
        switch (this.wsMST.view) {
            case 'archive': {
                itemIds = this.wsMST.archivedIds.toJSON();
                break;
            }
            case 'trash': {
                itemIds = this.wsMST.trashedIds.toJSON();
                break;
            }
            case 'main': {
                const orderedRoot = this.getOrderedMainRootItemIds(this.wsMST.childIds.toJSON());
                itemIds = orderedRoot.itemIds;
                pinnedLength = orderedRoot.pinnedLength;
                break;
            }
            case 'recent':
            case 'active':
            default:
                itemIds = [];
                break;
        }
        itemIds = ensureOnlyActualDataPassed(itemIds, this.wsMST);

        this._reachableItemId = new Set(this.wsMST.hiddenItems);

        items[this.wsMST.view] = {
            id: this.wsMST.view,
            childrenIds: (this.uniqueChildIds(itemIds, this.wsMST.view)[0]),
            get parentId() {
                return this.id.split('/').slice(0, -1).join('/');
            },
            isCluster: true,
            isOpen: true,
            data: {} as any,
        }
        this.buildTreeItems(itemIds, this.wsMST.view, null, undefined, items);

        if (this.wsMST.view === 'main') {
            for (const library of this.wsMST.libraries) {
                const orderedLibraryRoot = this.getOrderedMainRootItemIds(library.childIds.toJSON(), library.id);
                const libraryItemIds = ensureOnlyActualDataPassed(orderedLibraryRoot.itemIds, this.wsMST);
                const [formattedChildIds] = this.uniqueChildIds(libraryItemIds, library.id);

                if (this.wsMST.selectedLibrary?.id === library.id) {
                    pinnedLength = orderedLibraryRoot.pinnedLength;
                }

                this._reachableItemId.add(library.id);
                items[library.id] = {
                    id: library.id,
                    childrenIds: formattedChildIds,
                    get parentId() {
                        return this.id.split('/').slice(0, -1).join('/');
                    },
                    isCluster: true,
                    isOpen: true,
                    data: {
                        item: library,
                        hadRecursion: false,
                        parentItemId: null,
                        parentIdPath: null,
                    },
                }
                this.buildTreeItems(libraryItemIds, library.id, library.id, undefined, items);
            }
        }

        /* todo some cluster items may become lost if nearly simultaneously moved
            to cluster that was moved by someone else to a child cluster of the target one [server-sync-issue] */
        const lostItems = [];
        for (const [itemId, itemMST] of this.wsMST.items) {
            if (!this._reachableItemId.has(itemId)) {
                let shouldHaveBeenVisible = false;
                switch (this.wsMST.view) {
                    case 'archive':
                        shouldHaveBeenVisible = itemMST.archived === true;
                        break;
                    case 'trash':
                        shouldHaveBeenVisible = itemMST.trashed === true;
                        break;
                    case 'main':
                        shouldHaveBeenVisible = itemMST.trashed !== true && itemMST.archived !== true;
                        break;
                }
                if (shouldHaveBeenVisible) {
                    lostItems.push(itemId);
                }
            } else {
            }
        }
        if (lostItems.length) {
            this.buildTreeItems(lostItems, this.wsMST.view, null, undefined, items);
            items[this.wsMST.view] = {
                ...items[this.wsMST.view],
                childrenIds: (this.uniqueChildIds([...itemIds, ...lostItems], this.wsMST.view)[0])
            }
        }
        /* lost items end [server-sync-issue] */
        return {
            items: items,
            pinnedLength: pinnedLength,
        }
    }

    private getOrderedMainRootItemIds(itemIds: string[], libraryId?: string) {
        const pinnedItemIds = Array.from(this.wsMST.items.values())
            .filter(item => isPinned(item))
            .filter(item => libraryId === undefined || this.wsMST.findLibraryIdForItem(item.id) === libraryId)
            .map(item => item.id);
        const pinnedSet = new Set(pinnedItemIds);

        return {
            itemIds: [...pinnedItemIds, ...itemIds.filter(itemId => !pinnedSet.has(itemId))],
            pinnedLength: pinnedItemIds.length,
        };
    }

    buildTreeItems(itemIds: string[], parentIdPath: string, parentItemId: string | null, _refs = new WeakSet(), items: Record<string, DocumentItem>) {
        for (const itemId of itemIds) {
            if (itemId === parentItemId) {
                continue;
            }
            const itemMST = this.wsMST.items.get(itemId)!;
            if (itemMST === undefined) {
                captureMessageWrapper(`item ${itemId} not found in workspace, but present in childs of ${parentItemId}`);
                continue;
            }
            const hadRecursion = _refs.has(itemMST);
            _refs.add(itemMST)
            const [treeItem, childIds] = this._formatTreeItem(itemMST, parentIdPath, parentItemId, _refs, hadRecursion);
            if (!hadRecursion) {
                _refs.delete(itemMST);
            }
            items[treeItem.id] = treeItem;

            if (childIds.length) {
                if (hadRecursion) {
                    continue;
                }
                _refs.add(itemMST);
                this.buildTreeItems(childIds, treeItem.id, itemId, _refs, items);
                _refs.delete(itemMST);
            }
        }
    }

    _formatTreeItem(
        itemMST: ItemModelType,
        parentIdPath: string,
        parentItemId: string | null,
        recursionDetection: WeakSet<ItemModelType>,
        hadRecursion: boolean = false
    ): [treeItem: DocumentItem, childIds: string[]] {
        const itemIdPath = this._formId(itemMST.id, parentIdPath);
        const [formattedChildIds, childIds] = this.uniqueChildIds(itemMST.childIds, itemIdPath, recursionDetection);
        return [new DocumentTreeItem(itemIdPath, itemMST, formattedChildIds, {
            hadRecursion: hadRecursion,
            parentItemId: parentItemId,
            parentIdPath: parentIdPath,
        }), childIds]
    }

    _formId = (itemId: string, parentIdPath: string) => {
        this._reachableItemId.add(itemId);
        return `${parentIdPath}/${shortenId(itemId)}`;
    }

    uniqueChildIds = (childIds: string[], parentIdPath: string, recursionDetection: WeakSet<ItemModelType> = new Set()) => {
        const childIdsFormatted = [];
        const uniqueSet = new Set();
        const filteredChildIds = [];
        for (const childId of childIds) {
            if (!this.wsMST.items.get(childId)) {
                /* broken childs array already reported to sentry */
                continue;
            }
            if (uniqueSet.has(childId)) {
                /*
                save actions on one app validate to ensure unique childs,
                but sync events form other user actions can still break the logic todo add server side validation [server-sync-issue]
                * */
                continue;
            }
            uniqueSet.add(childId);

            if (recursionDetection.has(this.wsMST.items.get(childId)!)) {
                /* filter self recursion as a safeguard to not break UI if the save logic fails to validate
                    and prevent the recursion in the first place for any reason
                 */
                continue;
            }
            filteredChildIds.push(childId);
            childIdsFormatted.push(this._formId(childId, parentIdPath));
        }
        return [childIdsFormatted, filteredChildIds];
    }
}

export const shortenId = (id: string) => id.substring(0, 6);

const WORKSPACE_ROOT_IDS = new Set(['main', 'trash', 'archive']);

const isWorkspaceRootId = (id: string) => WORKSPACE_ROOT_IDS.has(id);

/* some unexpected bugs can potentially lead to inconsistency of the workspace childIds/trashedIds/archivedIds LGC-120
* any drag action will then fix the data consistency if we fix the arrays here */
function ensureOnlyActualDataPassed(itemIds: string[], workspace: WorkspaceModelType) {
    let checkedItemIds = [];
    for (const itemId of itemIds) {
        const item = workspace.items.get(itemId);
        if (!item) {
            captureMessageWrapper(`TreeBuilder not found workspace ${workspace.view} item - ${itemId}`);
            continue;
        }
        if (workspace.view === 'trash') {
            if (item.trashed !== true) {
                captureMessageWrapper(`TreeBuilder not trashed item found in workspace.trashedIds - ${itemId}`);
                continue;
            }
        } else if (workspace.view === 'archive') {
            if (item.archived !== true) {
                captureMessageWrapper(`TreeBuilder not archived item found in workspace.archivedIds - ${itemId}`);
                continue;
            }
        } else {
            if (item.trashed === true || item.archived === true) {
                captureMessageWrapper(`TreeBuilder archived or trashed item found in workspace.childIds - ${itemId}`);
                continue;
            }
        }
        checkedItemIds.push(itemId);
    }
    return checkedItemIds;
}
